/**
 * WhatsApp Configuration – Meta Cloud API settings per company (sales bill + future payment reminder).
 * Also includes Desktop WhatsApp session management (Approach B - whatsapp-web.js for auto-send).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  CardContent,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Breadcrumbs,
  Link,
  Paper,
  Switch,
  FormControlLabel,
  Checkbox,
  Divider,
  InputAdornment,
  IconButton,
  Alert,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import whatsappSettingsService from '../../services/whatsappSettingsService';
import { getUserRole } from '../../services/authService';
import { isDesktopApp } from '../../utils/whatsappShareHelper';

const MASKED_TOKEN = '********';

const defaultForm = () => ({
  enabled: false,
  apiVersion: 'v18.0',
  phoneNumberId: '',
  accessToken: '',
  tokenConfigured: false,
  templateName: '',
  templateLanguage: 'en_US',
  templateBodyParameterKeys: 'customer_name,bill_no,bill_date,bill_total,company_name',
  templateHeaderParameterKeys: '',
  useLegacyTextFallback: false,
  usageSalesBill: true,
  usagePaymentReminder: false,
});

const WhatsAppConfigurationPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const role = getUserRole();
  const canEdit = role === 'ADMIN' || role === 'MANAGER';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');

  // Desktop WhatsApp state (Approach B - whatsapp-web.js)
  const isDesktop = isDesktopApp();
  const [waStatus, setWaStatus] = useState({ 
    initialized: false, 
    ready: false, 
    authenticated: false, 
    qrCode: null, 
    error: null 
  });
  const [waLoading, setWaLoading] = useState(false);

  const checkWhatsAppStatus = useCallback(async () => {
    if (!isDesktop || !window.electronAPI) return;
    try {
      const status = await window.electronAPI.getWhatsAppStatus();
      setWaStatus(status);
    } catch (e) {
      console.error('Error checking WhatsApp status:', e);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (isDesktop && window.electronAPI) {
      checkWhatsAppStatus();
      const interval = setInterval(checkWhatsAppStatus, 3000);

      // Set up event listeners for WhatsApp status changes
      window.electronAPI.onWhatsAppQR((qr) => {
        setWaStatus(prev => ({ ...prev, qrCode: qr, authenticated: false }));
        setWaLoading(false);
      });
      
      window.electronAPI.onWhatsAppReady(() => {
        setWaStatus(prev => ({ ...prev, ready: true, authenticated: true, qrCode: null }));
        enqueueSnackbar('WhatsApp connected successfully!', { variant: 'success' });
        setWaLoading(false);
      });
      
      window.electronAPI.onWhatsAppAuthenticated(() => {
        setWaStatus(prev => ({ ...prev, authenticated: true, qrCode: null }));
      });
      
      window.electronAPI.onWhatsAppDisconnected((reason) => {
        setWaStatus({ initialized: false, ready: false, authenticated: false, qrCode: null, error: reason });
        enqueueSnackbar('WhatsApp disconnected: ' + reason, { variant: 'warning' });
        setWaLoading(false);
      });
      
      window.electronAPI.onWhatsAppAuthFailure((msg) => {
        setWaStatus(prev => ({ ...prev, error: msg, authenticated: false }));
        enqueueSnackbar('WhatsApp authentication failed: ' + msg, { variant: 'error' });
        setWaLoading(false);
      });

      return () => clearInterval(interval);
    }
  }, [isDesktop, checkWhatsAppStatus, enqueueSnackbar]);

  const handleConnectWhatsApp = async () => {
    if (!window.electronAPI) return;
    setWaLoading(true);
    setWaStatus(prev => ({ ...prev, error: null }));
    try {
      const res = await window.electronAPI.initializeWhatsApp();
      if (res && res.success === false) {
        enqueueSnackbar(res.error || 'Failed to start WhatsApp', { variant: 'error' });
        setWaLoading(false);
        return;
      }
      enqueueSnackbar('Connecting to WhatsApp... Please wait for QR code', { variant: 'info' });
    } catch (e) {
      enqueueSnackbar('Failed to connect WhatsApp: ' + e.message, { variant: 'error' });
      setWaLoading(false);
    }
  };

  const handleLogoutWhatsApp = async () => {
    if (!window.electronAPI) return;
    setWaLoading(true);
    try {
      await window.electronAPI.logoutWhatsApp();
      setWaStatus({ initialized: false, ready: false, authenticated: false, qrCode: null, error: null });
      enqueueSnackbar('WhatsApp logged out successfully', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Failed to logout from WhatsApp', { variant: 'error' });
    } finally {
      setWaLoading(false);
    }
  };

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await whatsappSettingsService.get();
      setForm({
        enabled: Boolean(data.enabled),
        apiVersion: data.apiVersion || 'v18.0',
        phoneNumberId: data.phoneNumberId || '',
        accessToken: data.accessToken || '',
        tokenConfigured: Boolean(data.tokenConfigured),
        templateName: data.templateName || '',
        templateLanguage: data.templateLanguage || 'en_US',
        templateBodyParameterKeys:
          data.templateBodyParameterKeys != null && data.templateBodyParameterKeys !== ''
            ? data.templateBodyParameterKeys
            : 'customer_name,bill_no,bill_date,bill_total,company_name',
        templateHeaderParameterKeys: data.templateHeaderParameterKeys || '',
        useLegacyTextFallback: Boolean(data.useLegacyTextFallback),
        usageSalesBill: data.usageSalesBill !== false,
        usagePaymentReminder: Boolean(data.usagePaymentReminder),
      });
    } catch (e) {
      enqueueSnackbar('Failed to load WhatsApp settings', { variant: 'error' });
      setForm(defaultForm());
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: v }));
  };

  const buildSavePayload = () => {
    const payload = {
      enabled: form.enabled,
      apiVersion: (form.apiVersion || '').trim() || 'v18.0',
      phoneNumberId: (form.phoneNumberId || '').trim(),
      templateName: (form.templateName || '').trim(),
      templateLanguage: (form.templateLanguage || '').trim() || 'en_US',
      templateBodyParameterKeys: (form.templateBodyParameterKeys || '').trim(),
      templateHeaderParameterKeys: (form.templateHeaderParameterKeys || '').trim(),
      useLegacyTextFallback: form.useLegacyTextFallback,
      usageSalesBill: form.usageSalesBill,
      usagePaymentReminder: form.usagePaymentReminder,
    };
    const token = (form.accessToken || '').trim();
    if (token && token !== MASKED_TOKEN) {
      payload.accessToken = token;
    }
    return payload;
  };

  const handleSave = async () => {
    if (!canEdit) return;
    if (form.enabled) {
      if (!form.phoneNumberId.trim()) {
        enqueueSnackbar('Phone Number ID is required when WhatsApp is enabled', { variant: 'warning' });
        return;
      }
      if (!form.templateName.trim()) {
        enqueueSnackbar('Template name is required when WhatsApp is enabled (or disable and use legacy text)', {
          variant: 'warning',
        });
        return;
      }
      if (!form.usageSalesBill && !form.usagePaymentReminder) {
        enqueueSnackbar('Select at least one usage: Sales bill or Payment reminder', { variant: 'warning' });
        return;
      }
      const hasToken = form.tokenConfigured || (form.accessToken && form.accessToken !== MASKED_TOKEN);
      if (!hasToken) {
        enqueueSnackbar('Access token is required when WhatsApp is enabled', { variant: 'warning' });
        return;
      }
    }

    try {
      setSaving(true);
      await whatsappSettingsService.save(buildSavePayload());
      enqueueSnackbar('WhatsApp settings saved', { variant: 'success' });
      await loadSettings();
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        (typeof e.response?.data === 'string' ? e.response.data : null) ||
        e.message ||
        'Failed to save';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestMessage = async () => {
    if (!canEdit) return;
    const phone = (testPhone || '').trim();
    if (!phone) {
      enqueueSnackbar('Enter a test phone number (country code + digits, no spaces)', { variant: 'warning' });
      return;
    }
    try {
      setTesting(true);
      const res = await whatsappSettingsService.sendTestMessage({
        phone,
        message: (testMessage || '').trim(),
      });
      if (res.success) {
        enqueueSnackbar(res.message || 'Test message sent', { variant: 'success' });
      } else {
        enqueueSnackbar(res.message || 'Test failed', { variant: 'error' });
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Failed to send test message';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/dashboard" color="inherit" underline="hover">
            Dashboard
          </Link>
          <Link component={RouterLink} to="/settings/company" color="inherit" underline="hover">
            Settings
          </Link>
          <Typography color="text.primary">WhatsApp Configuration</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ChatIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              WhatsApp Configuration
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Connect your Meta WhatsApp Business Cloud API to send sales bill notifications to customers. Payment reminder
            options are stored for future use. Template parameter keys map to variables in Meta (e.g. customer_name, bill_no,
            bill_date, bill_total, company_name).
          </Typography>
        </Box>

        {/* Desktop WhatsApp Auto-Send (Approach B - whatsapp-web.js) */}
        {isDesktop && (
          <Paper elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DesktopWindowsIcon sx={{ mr: 1.5, color: waStatus.ready ? 'success.main' : 'grey.500' }} />
                <Typography variant="h6">Desktop WhatsApp Auto-Send</Typography>
                <Chip
                  icon={waStatus.ready ? <CheckCircleIcon /> : undefined}
                  label={waStatus.ready ? 'Connected' : waStatus.qrCode ? 'Scan QR Code' : waStatus.initialized ? 'Connecting...' : 'Disconnected'}
                  color={waStatus.ready ? 'success' : waStatus.qrCode ? 'warning' : 'default'}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </Box>
              
              <Alert severity={waStatus.ready ? 'success' : 'info'} sx={{ mb: 3 }}>
                <Typography variant="body2">
                  {waStatus.ready ? (
                    <><strong>Connected!</strong> Bills will be automatically sent as images to customers' WhatsApp when you create a sale.</>
                  ) : (
                    <><strong>Desktop Auto-Send Feature:</strong> Connect your WhatsApp to automatically send bills as images directly to customers. 
                    No manual download or attachment needed. Session persists across app restarts.</>
                  )}
                </Typography>
              </Alert>

              {waStatus.error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {waStatus.error}
                </Alert>
              )}

              {/* QR Code Display */}
              {waStatus.qrCode && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, p: 3, bgcolor: 'white', borderRadius: 2, border: '2px solid #25D366' }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#25D366' }}>
                    Scan this QR code with your WhatsApp mobile app
                  </Typography>
                  <img 
                    src={waStatus.qrCode} 
                    alt="WhatsApp QR Code" 
                    style={{ width: 256, height: 256, borderRadius: 8 }} 
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                    Open WhatsApp on your phone → Settings → Linked Devices → Link a Device
                  </Typography>
                </Box>
              )}

              {/* Loading spinner when connecting */}
              {waLoading && !waStatus.qrCode && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, p: 3 }}>
                  <CircularProgress size={40} sx={{ color: '#25D366' }} />
                  <Typography sx={{ ml: 2 }}>Connecting to WhatsApp...</Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {!waStatus.ready && !waStatus.qrCode && (
                  <Button
                    variant="contained"
                    sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
                    startIcon={waLoading ? <CircularProgress size={20} color="inherit" /> : <QrCode2Icon />}
                    onClick={handleConnectWhatsApp}
                    disabled={waLoading}
                  >
                    Connect WhatsApp
                  </Button>
                )}
                {(waStatus.ready || waStatus.authenticated) && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={waLoading ? <CircularProgress size={20} /> : <LogoutIcon />}
                    onClick={handleLogoutWhatsApp}
                    disabled={waLoading}
                  >
                    Logout / Disconnect
                  </Button>
                )}
              </Box>

              {waStatus.ready && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Your WhatsApp is connected. When you create a sale for a customer with a phone number, 
                  the bill will be automatically sent as an image to their WhatsApp.
                </Typography>
              )}
            </CardContent>
          </Paper>
        )}

        <Paper elevation={2}>
          <CardContent sx={{ p: 4 }}>
            <FormControlLabel
              control={<Switch checked={form.enabled} onChange={handleChange('enabled')} disabled={!canEdit} />}
              label="Enable WhatsApp messaging for this company"
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              API credentials
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="API version"
                  value={form.apiVersion}
                  onChange={handleChange('apiVersion')}
                  disabled={!canEdit}
                  placeholder="v18.0"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Phone number ID"
                  value={form.phoneNumberId}
                  onChange={handleChange('phoneNumberId')}
                  disabled={!canEdit}
                  helperText="From Meta Business Suite / WhatsApp > API setup"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Access token"
                  type={showToken ? 'text' : 'password'}
                  value={form.accessToken}
                  onChange={handleChange('accessToken')}
                  disabled={!canEdit}
                  placeholder={form.tokenConfigured ? 'Leave as-is or enter a new token' : 'Permanent or long-lived token'}
                  helperText={
                    form.tokenConfigured
                      ? 'Token is stored. Enter a new value only when rotating the token.'
                      : 'Required when enabling WhatsApp'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle token visibility"
                          onClick={() => setShowToken((s) => !s)}
                          edge="end"
                          disabled={!canEdit}
                        >
                          {showToken ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Template
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Template name"
                  value={form.templateName}
                  onChange={handleChange('templateName')}
                  disabled={!canEdit}
                  placeholder="e.g. sales_bill_notification"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Template language"
                  value={form.templateLanguage}
                  onChange={handleChange('templateLanguage')}
                  disabled={!canEdit}
                  placeholder="en_US"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Body parameter keys (comma-separated, order = {{1}}, {{2}}, …)"
                  value={form.templateBodyParameterKeys}
                  onChange={handleChange('templateBodyParameterKeys')}
                  disabled={!canEdit}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Header parameter keys (optional)"
                  value={form.templateHeaderParameterKeys}
                  onChange={handleChange('templateHeaderParameterKeys')}
                  disabled={!canEdit}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.useLegacyTextFallback}
                      onChange={handleChange('useLegacyTextFallback')}
                      disabled={!canEdit}
                    />
                  }
                  label="Allow legacy plain-text message when template name is empty (emergency only; Meta usually requires templates)"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Usage
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={form.usageSalesBill} onChange={handleChange('usageSalesBill')} disabled={!canEdit} />}
              label="Sales bill message (send to customer mobile after billing)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.usagePaymentReminder}
                  onChange={handleChange('usagePaymentReminder')}
                  disabled={!canEdit}
                />
              }
              label="Payment reminder (saved for future automation; not sent yet)"
            />

            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!canEdit || saving}
              >
                Save
              </Button>
            </Box>

            {!canEdit && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Only Admin or Manager can change these settings. You can view the configuration above.
              </Typography>
            )}

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" gutterBottom>
              Test message
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sends a simple text message using your saved token and phone number ID (Admin / Manager only).
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Test phone"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  disabled={!canEdit}
                  placeholder="919876543210"
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Message (optional)"
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  disabled={!canEdit}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={20} /> : <SendIcon />}
                  onClick={handleTestMessage}
                  disabled={!canEdit || testing}
                  fullWidth
                >
                  Send test
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Paper>
      </Container>
    </Layout>
  );
};

export default WhatsAppConfigurationPage;
